import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { EASE_SPRING, ink } from '../lib/theme';
import { IconX } from './icons';

export interface Origin { x: number; y: number }

/** Captura o ponto clicado — é dele que o modal "nasce" (PRD §4-A nível 2). */
export function originFrom(e: { clientX: number; clientY: number }): Origin {
  return { x: e.clientX, y: e.clientY };
}

/**
 * Scale Modal (PRD §4-A, nível 2): nasce escalando a partir do elemento clicado,
 * estilo macOS — `transform-origin` no ponto de origem, easing de mola, fundo
 * escurecido com desfoque. Fecha revertendo a mesma animação.
 * Fecha com Esc, clique fora ou ✕.
 */
export function ScaleModal({ open, origin, onClose, title, subtitle, width = 980, headerExtra, children }: {
  open: boolean;
  origin: Origin | null;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: number;
  /** Conteúdo extra no header, entre o título e o ✕ — pra controles que
   * precisam estar sempre visíveis mesmo com o corpo rolado (ex: o toggle
   * "Corrigir" do `EvidenceModal`), não porque "cabe" ali. */
  headerExtra?: ReactNode;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) { setMounted(true); setClosing(false); }
  }, [open]);

  // Fecha com Esc. Registrado só enquanto aberto pra não competir com outros
  // handlers de teclado (os atalhos de auditoria do §9-C, por exemplo).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); requestClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function requestClose() {
    setClosing(true);
    // Espera a animação de volta terminar antes de desmontar — sem isso o modal
    // sumiria seco em vez de reverter pro ponto de origem.
    setTimeout(() => { setMounted(false); setClosing(false); onClose(); }, 190);
  }

  if (!open && !mounted) return null;

  // transform-origin em % relativo ao card: converte o ponto de clique da
  // viewport pra dentro da caixa do modal, que é o que o CSS espera.
  const ox = origin ? `${((origin.x - (window.innerWidth - Math.min(width, window.innerWidth - 60)) / 2) / Math.min(width, window.innerWidth - 60)) * 100}%` : '50%';
  const oy = origin ? `${Math.max(-40, Math.min(140, ((origin.y - window.innerHeight * 0.12) / (window.innerHeight * 0.76)) * 100))}%` : '50%';

  const active = open && !closing;

  // Portal pro <body>: o modal é renderizado dentro da página, que vive num
  // grid com `position: relative; z-index: 2`. Esse z-index cria um contexto de
  // empilhamento — sem o portal, o `z-index: 200` daqui vale só DENTRO dele, e
  // o modal aparece atrás do header (60) e da barra de filtros (55).
  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={title}
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 30,
        background: 'rgba(10,10,18,0.62)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
        opacity: active ? 1 : 0, transition: 'opacity 0.19s ease',
      }}
    >
      <div
        ref={cardRef}
        style={{
          width: Math.min(width, window.innerWidth - 60), maxHeight: '86vh', display: 'flex', flexDirection: 'column',
          background: 'var(--modal-bg)', backdropFilter: 'blur(30px) saturate(1.5)', WebkitBackdropFilter: 'blur(30px) saturate(1.5)',
          border: `1px solid ${ink(0.13)}`, borderRadius: 22, boxShadow: '0 40px 110px rgba(8,8,16,0.72)',
          transformOrigin: `${ox} ${oy}`,
          transform: active ? 'scale(1)' : 'scale(0.82)',
          opacity: active ? 1 : 0,
          transition: `transform 0.28s ${EASE_SPRING}, opacity 0.19s ease`,
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, padding: '20px 26px 18px',
          borderBottom: `1px solid ${ink(0.08)}`, flex: 'none',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 780, letterSpacing: '-0.018em' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: ink(0.46), marginTop: 3 }}>{subtitle}</div>}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
            {headerExtra}
            <button
              type="button" onClick={requestClose} aria-label="Fechar"
              className="ko-ghost"
              style={{
                width: 34, height: 34, borderRadius: 11, flex: 'none',
                border: `1px solid ${ink(0.15)}`, background: ink(0.06), color: ink(0.62),
                cursor: 'pointer', font: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            ><IconX size={14} /></button>
          </div>
        </div>
        <div style={{ padding: '22px 26px 26px', overflow: 'auto', minHeight: 0 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
